import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowUpRight, Truck, ShieldCheck, Sparkles, Minus, Plus, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useCart, useWishlist } from "@/contexts/ShopContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { trackProductView } from "@/hooks/useTracking";
import { applySEO, productSchema, breadcrumbSchema, productImageAlt, SITE_URL } from "@/lib/seo";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { currency, formatAmount } = useCurrency();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((res) => {
        const data = res.data;
        setProduct(data);
        const firstSize = (data.sizes || []).find((s) => s.in_stock);
        const firstColor = (data.colors || []).find((c) => c.in_stock);
        setSize(firstSize?.label || null);
        setColor(firstColor?.name || null);
        setActiveImage(0);
        trackProductView(id);
        try {
          applySEO({
            title: `${data.name} — ${data.category} | Asymmetry Edits`.slice(0, 65),
            description: (data.tagline || data.description || "").slice(0, 158),
            canonical: `${SITE_URL}/product/${data.id}`,
            image: data.image,
            og: { type: "product" },
            jsonLd: [
              { id: "ld-pdp-product", data: productSchema(data, "INR") },
              {
                id: "ld-pdp-breadcrumb",
                data: breadcrumbSchema([
                  { name: "Home", url: "/" },
                  { name: data.category, url: `/#collections` },
                  { name: data.name, url: `/product/${data.id}` },
                ]),
              },
            ],
          });
        } catch (e) { console.warn("SEO apply failed:", e); }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight">
        <Navbar />
        <div className="pt-32 px-6 md:px-12 lg:px-24 grid md:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-midnight-panel animate-pulse" />
          <div className="space-y-6">
            <div className="h-4 w-32 bg-midnight-panel animate-pulse" />
            <div className="h-12 w-3/4 bg-midnight-panel animate-pulse" />
            <div className="h-24 bg-midnight-panel animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-midnight">
        <Navbar />
        <div className="pt-40 text-center">
          <div className="eyebrow mb-4">Not found</div>
          <h2 className="font-serif text-white text-4xl">This piece is no longer available.</h2>
          <Link to="/" className="mt-8 inline-block text-silver tracking-[0.32em] uppercase text-[11px] border-b border-silver">
            Back to the house
          </Link>
        </div>
      </div>
    );
  }

  const price = currency === "INR" ? product.price_inr : product.price_usd;
  const lowStock = product.low_stock;
  const inWish = has(product.id);

  const handleAddToCart = () => {
    if (product.sizes?.length > 1 && !size) { toast.error("Please select a size."); return; }
    if (product.colors?.length > 1 && !color) { toast.error("Please select a colour."); return; }
    if (product.stock_remaining <= 0) { toast.error("Out of stock."); return; }
    addItem({
      product_id: product.id,
      size: product.sizes?.length === 1 ? product.sizes[0].label : size,
      color: product.colors?.length === 1 ? product.colors[0].name : color,
      quantity: qty,
    });
    toast.success("Added to the bag.", { description: product.name });
  };

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-24" data-testid="product-detail">
        <Link to="/" className="inline-flex items-center gap-2 text-white/55 hover:text-white text-[11px] tracking-[0.3em] uppercase mb-8" data-testid="pdp-back">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-2 hidden md:flex flex-col gap-3">
                {(product.images || [product.image]).map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    aria-label={`Image ${i + 1}`}
                    data-testid={`pdp-thumb-${i}`}
                    className={`aspect-[3/4] overflow-hidden border ${activeImage === i ? "border-silver" : "border-white/10"}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              <motion.div
                key={activeImage}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                className="col-span-12 md:col-span-10 aspect-[4/5] bg-midnight-panel overflow-hidden"
              >
                <img
                  src={(product.images && product.images[activeImage]) || product.image}
                  alt={productImageAlt(product, activeImage)}
                  className="w-full h-full object-cover"
                  data-testid="pdp-main-image"
                />
              </motion.div>
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-5">
            <div className="eyebrow text-silver/70 mb-3">{product.category} · {product.sku}</div>
            <h1 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]" data-testid="pdp-name">
              {product.name}
            </h1>
            <p className="mt-4 text-white/70 text-base md:text-lg font-light leading-relaxed">{product.tagline}</p>

            <div className="mt-8 flex items-end gap-4 border-y border-white/8 py-6">
              <div className="font-serif text-white text-3xl md:text-4xl" data-testid="pdp-price">
                {formatAmount(price)}
              </div>
              {lowStock && (
                <div data-testid="pdp-low-stock" className="text-[10px] tracking-[0.32em] uppercase text-silver">
                  Only {product.stock_remaining} left
                </div>
              )}
              {product.stock_remaining <= 0 && (
                <div className="text-[10px] tracking-[0.32em] uppercase text-white/50">Sold out</div>
              )}
            </div>

            {/* Color */}
            {product.colors?.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Colour</span>
                  <span className="text-white/60 text-sm">{color || "—"}</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      disabled={!c.in_stock}
                      onClick={() => setColor(c.name)}
                      data-testid={`pdp-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      title={c.name + (c.in_stock ? "" : " — unavailable")}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                        color === c.name ? "border-silver scale-110" : "border-white/15"
                      } ${!c.in_stock ? "opacity-30 line-through" : "hover:border-white/50"}`}
                      style={{ backgroundColor: c.hex }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size */}
            {product.sizes?.length > 1 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Size</span>
                  <button className="text-[10px] tracking-[0.25em] uppercase text-white/45 hover:text-white">
                    Size guide
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      disabled={!s.in_stock}
                      onClick={() => setSize(s.label)}
                      data-testid={`pdp-size-${s.label.toLowerCase()}`}
                      className={`min-w-[56px] py-2.5 px-4 border text-[11px] tracking-[0.2em] uppercase transition-all ${
                        size === s.label ? "border-silver bg-white/5 text-white" : "border-white/15 text-white/70 hover:border-white/40"
                      } ${!s.in_stock ? "opacity-30 line-through cursor-not-allowed" : ""}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Qty + CTA */}
            <div className="mt-10 flex items-stretch gap-3">
              <div className="inline-flex items-center border border-white/20">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  aria-label="Decrease"
                  className="px-3 py-3 text-white/70 hover:text-white"
                  data-testid="pdp-qty-dec"
                ><Minus size={14} /></button>
                <span className="px-4 text-white text-sm">{qty}</span>
                <button
                  onClick={() => setQty(Math.min(10, qty + 1))}
                  aria-label="Increase"
                  className="px-3 py-3 text-white/70 hover:text-white"
                  data-testid="pdp-qty-inc"
                ><Plus size={14} /></button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={product.stock_remaining <= 0}
                data-testid="pdp-add-to-cart"
                className="flex-1 bg-white text-black py-4 uppercase tracking-[0.32em] text-[11px] hover:bg-silver disabled:opacity-40 transition-colors"
              >
                {product.stock_remaining <= 0 ? "Sold out" : "Add to Bag"}
              </button>
              <button
                onClick={() => toggle(product.id)}
                aria-label="Wishlist"
                data-testid="pdp-wishlist"
                className={`w-14 h-14 border flex items-center justify-center transition-all ${
                  inWish ? "border-silver bg-white/5 text-white" : "border-white/20 text-white/70 hover:border-white"
                }`}
              >
                <Heart size={18} strokeWidth={1.2} fill={inWish ? "currentColor" : "transparent"} />
              </button>
            </div>

            {/* Trust strip */}
            <div className="mt-8 grid grid-cols-3 gap-3 border-t border-white/8 pt-6 text-[10px] tracking-[0.25em] uppercase text-white/55">
              <div className="flex items-center gap-2"><Truck size={14} strokeWidth={1.2} /> Insured shipping</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} strokeWidth={1.2} /> 2-yr repair</div>
              <div className="flex items-center gap-2"><Sparkles size={14} strokeWidth={1.2} /> Numbered piece</div>
            </div>

            {/* Description */}
            <div className="mt-12 space-y-6">
              <div>
                <div className="eyebrow text-silver/70 mb-2">Description</div>
                <p className="text-white/70 font-light leading-relaxed">{product.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="eyebrow text-silver/70 mb-2">Fabric & Materials</div>
                  <p className="text-white/70 font-light text-sm leading-relaxed">{product.fabric}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(product.materials || []).map((m) => (
                      <span key={m} className="text-[10px] tracking-[0.2em] uppercase text-white/55 border border-white/10 px-2 py-1">{m}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="eyebrow text-silver/70 mb-2">Care</div>
                  <p className="text-white/70 font-light text-sm leading-relaxed">{product.care}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {product.related?.length > 0 && (
          <section className="mt-24 md:mt-36" data-testid="pdp-related">
            <div className="flex items-end justify-between border-b border-white/8 pb-6 mb-10">
              <div>
                <div className="eyebrow text-silver/70 mb-2">You may also like</div>
                <h2 className="font-serif text-white text-3xl md:text-4xl">From the same edit.</h2>
              </div>
              <Link to="/" className="text-[11px] tracking-[0.32em] uppercase text-white/70 hover:text-white border-b border-white/30 pb-1">
                View all
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
              {product.related.map((r) => (
                <Link
                  key={r.id}
                  to={`/product/${r.id}`}
                  data-testid={`related-${r.id}`}
                  className="group block"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-midnight-panel mb-4">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover product-img" />
                  </div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{r.category}</div>
                  <div className="font-serif text-white text-lg md:text-xl mt-1 leading-tight">{r.name}</div>
                  <div className="text-silver text-sm mt-1">
                    {formatAmount(currency === "INR" ? r.price_inr : r.price_usd)}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <FooterSection />
    </div>
  );
}
